import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Icon, IconColour } from './icon';
import { TextInput } from './input/textInput';
import { DateInput } from './input/dateInput';
import { SearchList } from './searchList';
import { NumberInput } from './input/numberInput';
import { ResetButton } from './resetButton';
import { TripleSwitch } from './tripleSwitch';
import Axios from 'axios';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import useSearch from 'hooks/useSearch';
import DateSelector from './input/dateSelector';

const Container = styled.div`
  width: 100%;
`;

// UI refresh: a white search box with the filter toggle, instead of the green bar.
const Bar = styled.div`
  flex-grow: 1;
  height: 48px;
  background-color: #ffffff;
  border: 1px solid #dadde2;
  border-radius: 12px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  box-sizing: border-box;
  color: #5b616e;
  user-select: none;
  padding: 0 4px 0 8px;
`;

const FilterComponent = styled.div`
  flex-grow: 1;
  margin-top: 8px;
  background-color: #ffffff;
  border: 1px solid #e4e6ea;
  border-radius: 12px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  box-sizing: border-box;
  color: #2e333b;
  user-select: none;
  padding: 6px 8px;
  overflow-x: scroll;
  white-space: nowrap;
  flex-wrap: wrap;
  ::-webkit-scrollbar {
    display: none;
  }
  box-sizing: border-box;
  padding-bottom: 3px;
  padding-right: 5px;
`;

const SearchContainer = styled.div`
  position: relative;
  padding-left: ${(p) => p.theme.space[1]}px;
  padding-right: ${(p) => p.theme.space[1]}px;
  flex-grow: 1;
`;

const CloseIconContainer = styled.div`
  position: absolute;
  top: 6px;
  right: 10px;
`;

const SearchError = styled.div`
  position: absolute;
  top: 45px;
  left: 0;
  width: 100%;
  z-index: 100;
  padding: 12px;
  background-color: ${(p) => p.theme.colors.white};
  color: ${(p) => p.theme.colors.primary};
`;

const Padding = styled.div`
  margin: 5px 0 2px 5px;
`;

type Props = {
  setFilter(
    budgetLow: number,
    budgetHigh: number,
    dateLow: Date,
    dateHigh: Date,
    completeState: 'complete' | 'incomplete' | 'all',
  ): void;
  /** Enter: every matching project for the page grid, and the words searched ('' clears the filter). */
  setSearchedProjects(items: IProject[], query: string): void;
};

const ProjectFilter: React.FC<Props> = ({ setFilter, setSearchedProjects }) => {
  const dispatch = useDispatch();
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchItems, setSearchItems] = useState<(IProject | IRecord | IVideo | INote)[]>([]);
  const [completeState, setCompleteState] = useState<'complete' | 'incomplete' | 'all'>('all');
  const [searchInputValue, setSearchInputValue] = useState('')

  const [budgetLow, setBudgetLow] = useState(0);
  const [budgetHigh, setBudgetHigh] = useState(10000000);
  const [dateLow, setDateLow] = useState(new Date(-1));
  const [dateHigh, setDateHigh] = useState(new Date(2049, 11));

  const [filterMenu, toggleFilterMenu] = useState(false);

  const { searchString, searchAll, preload } = useSearch();
  const [searchError, setSearchError] = useState('');
  // The search the page grid is filtered by (after Enter), and how many projects it found.
  const [filtered, setFiltered] = useState<{ query: string; count: number } | null>(null);

  const clearFilter = () => {
    setFiltered(null);
    setSearchedProjects([], '');
  };

  const search = async (input: string) => {
    if (input !== '') {
      try {
        const results = await searchString(input);
        setSearchError('');
        setSearchItems(results);
      } catch (e) {
        setSearchError((e as Error).message || 'Search failed.');
        setSearchItems([]);
      }
    } else {
      setSearchItems([]);
      clearFilter();
    }
  };

  // Enter (Aaron, 5 Nov 2026): fill the page with every matching project, best matches first.
  const setSearchValueAndSearch = async (input: string) => {
    const query = input.trim();
    if (!query) {
      clearFilter();
      return;
    }
    try {
      const results = await searchAll(query);
      setSearchError('');
      setSearchItems([]);
      setFiltered({ query, count: results.length });
      setSearchedProjects(results, query);
    } catch (e) {
      setSearchError((e as Error).message || 'Search failed.');
    }
  };

  const setSearch = (visible: boolean) => {
    if (visible) preload();
    if (!visible) {
      setTimeout(() => {
        setSearchVisible(false);
      }, 300);
    } else {
      setSearchVisible(true);
    }
  };

  const searchClick = async (id: string) => {
    dispatch(push(`/project/${id}`));
  };

  useEffect(() => {
    setFilter(budgetLow, budgetHigh, dateLow, dateHigh, completeState);
  }, [budgetLow, budgetHigh, dateLow, dateHigh, completeState]);

  const resetFilters = () => {
    setCompleteState('all');
    setBudgetLow(0);
    setBudgetHigh(10000000);
    setDateLow(new Date(-1));
    setDateHigh(new Date(2049, 11));
  };

  return (
    <Container>
      <Bar>
        <SearchContainer>
          <TextInput
            value={searchInputValue}
            setInput={setSearchInputValue}
            name={'projectSearch'}
            placeholder={'Search projects, clients, file names, notes…'}
            update={search}
            style={'primary'}
            noBorder
            isActive={setSearch}
            continuousUpdate={true}
            onEnterPress={setSearchValueAndSearch}
          />
          {searchVisible && <SearchList key={'Search List'} items={searchItems as IProject[]} click={searchClick} />}
          {searchVisible && searchError && <SearchError>{searchError}</SearchError>}
          {searchInputValue && (
            <CloseIconContainer>
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => {
                  setSearchInputValue('');
                  setSearchItems([]);
                  clearFilter();
                }}
                style={{ width: 28, height: 28, border: 'none', borderRadius: 8, background: '#F1F2F4', color: '#5B616E', fontSize: 18, lineHeight: 1, cursor: 'pointer' }}
              >
                ×
              </button>
            </CloseIconContainer>
          )}
        </SearchContainer>
        <button
          type="button"
          className="archy-btn"
          aria-expanded={filterMenu}
          onClick={() => toggleFilterMenu(!filterMenu)}
          style={{
            height: 36,
            padding: '0 12px',
            borderRadius: 10,
            border: `1px solid ${filterMenu ? '#16181D' : '#DADDE2'}`,
            background: filterMenu ? '#16181D' : '#FFFFFF',
            color: filterMenu ? '#FFFFFF' : '#2E333B',
            font: 'inherit',
            fontSize: 13,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            whiteSpace: 'nowrap',
          }}
        >
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M4 6h16M7 12h10M10 18h4" />
          </svg>
          Status, date and budget
        </button>
      </Bar>
      {filtered && (
        <div
          role="status"
          style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 10, fontSize: 14, color: '#2E333B' }}
        >
          <span>
            {filtered.count === 0 ? (
              <>No projects match "<strong>{filtered.query}</strong>".</>
            ) : (
              <>
                Showing <strong>{filtered.count.toLocaleString()}</strong> project{filtered.count === 1 ? '' : 's'} matching "
                <strong>{filtered.query}</strong>", best matches first.
              </>
            )}
          </span>
          <button
            type="button"
            className="archy-btn"
            onClick={() => {
              setSearchInputValue('');
              setSearchItems([]);
              clearFilter();
            }}
            style={{ height: 32, padding: '0 12px', borderRadius: 8, border: '1px solid #DADDE2', background: '#FFFFFF', color: '#16181D', font: 'inherit', fontSize: 13, cursor: 'pointer' }}
          >
            Clear search
          </button>
        </div>
      )}
      {filterMenu && (
        <FilterComponent>
          <Padding>
            <TripleSwitch stateChange={setCompleteState} state={completeState} />
          </Padding>
          <Padding>{completeState}</Padding>
          <Padding>projects from</Padding>
          <Padding>
            <DateSelector initialDate={dateLow} updated={setDateLow} altStyle allowUpdate />
          </Padding>
          <Padding>to</Padding>
          <Padding>
            <DateSelector initialDate={dateHigh} updated={setDateHigh} altStyle allowUpdate />
          </Padding>
          <Padding>with budget of </Padding>
          <Padding>
            $&nbsp;
            <NumberInput
              value={budgetLow}
              name={'budgetmin'}
              update={setBudgetLow}
              style={'secondary'}
              width={'80px'}
            />
          </Padding>
          <Padding>to</Padding>
          <Padding>
            $&nbsp;
            <NumberInput
              value={budgetHigh}
              name={'budgetmax'}
              update={setBudgetHigh}
              style={'secondary'}
              width={'100px'}
            />
          </Padding>
          <Padding>
            <ResetButton text={'reset'} onClick={resetFilters} />
          </Padding>
        </FilterComponent>
      )}
    </Container>
  );
};

export default ProjectFilter;
